<template>
  <div class="select-value">
    <fs-input
      v-if="isSearchable"
      ref="searchInput"
      v-model="localSearchQuery"
      class="select-value__input"
      :placeholder="placeholder"
      :tabindex="0"
      :is-expanded="isOpened"
      is-search
      is-expandable
      @reset-input="resetInput"
      @update:isExpanded="$emit('toggle')"
    >
      <template v-if="$slots['search-prefix']" #prefix><slot name="search-prefix"></slot></template>
    </fs-input>

    <div
      v-else
      :class="{
        'select-value__select': true,
        'select-value__select--clearable': isClearable && valueIsSet,
        'select-value__select--tight': Boolean(position),
        'select-value__select--mid': variant === 'mid',
        'select-value__select--min-code': variant === 'min-code',
      }"
      @click="$emit('toggle')"
    >
      <span v-if="valueIsSet" class="select-value__selected-value">
        <slot></slot>
      </span>
      <span
        v-else
        :class="{
          'select-value__selected-value': true,
          'select-value__selected-value--placeholder': true,
        }"
      >
        {{ placeholder }}
      </span>

      <!-- temporary cross icon needed for admin, please refactor when overlocking this component -->
      <icon-button
        v-if="isClearable && valueIsSet"
        :icon="crossIcon"
        size="nano"
        class="select-value__cross-icon"
        @click.stop="resetInput"
      />
      <dropdown-caret
        :class="{
          'select-value__arrow': true,
          'select-value__arrow--tight': position === 'right',
          'select-value__arrow--tighter': position === 'left',
          'select-value__arrow--flip': isOpened,
        }"
        :is-flipped="isOpened"
      />
    </div>
  </div>
</template>

<script>
import twoWayProperty from '@lib/twoWayProperty';

// Components
import FsInput from '@components/form-elements/fs-input.vue';
import DropdownCaret from '@components/dropdown-caret.vue';
import crossIcon from '@icons/other/ico-cross.svg';
import IconButton from '@components/icon-button.vue';

export default {
  name: 'select-value',

  props: {
    value: {},

    placeholder: {
      type: String,
      default: '',
    },

    isSearchable: {
      type: Boolean,
      default: false,
    },

    isClearable: {
      type: Boolean,
      default: false,
    },

    searchQuery: {
      type: [String, Number],
    },

    isOpened: {
      type: Boolean,
      default: false,
    },

    position: {
      type: String,
      validator: (position) => ['left', 'right'].includes(position),
    },

    /**
     * Visual variant of the select - must match parent fs-select variant
     */
    variant: {
      type: String,
      default: 'standard',
      validator: (value) => ['standard', 'mid', 'min-code'].includes(value),
    },
  },

  data() {
    return {
      crossIcon,
    };
  },

  components: {
    IconButton,
    DropdownCaret,
    FsInput,
  },

  // TODO: this code causes the page to scroll down to where the element is located. It needs to be fixed.

  // mounted() {
  // setTimeout is needed to avoid auto scrolling of the page to the initial position of the popper
  // (initial position of all poppers is somewhere in the bottom of the page and it changes after opening of the popper.
  // That's how popper component works)
  //   setTimeout(() => this.focus(), 100);
  // },

  computed: {
    localValue: twoWayProperty('value'),
    localSearchQuery: twoWayProperty('searchQuery'),

    valueIsSet() {
      return this.localValue !== null && this.localValue !== undefined;
    },

    selectedValue() {
      if (this.valueIsSet) return this.localValue;

      return undefined;
    },
  },

  methods: {
    focus() {
      this.$refs.searchInput?.focus();
    },

    resetInput() {
      this.localSearchQuery = '';

      this.$emit('clear');
    },
  },
};
</script>

<style scoped lang="scss">
@import '@styles/variables';

// Select Value ------------------------------------------------------------
.select-value {
  &__input {
    background-color: transparent;
  }

  &__select {
    height: 3.7rem;
    flex-shrink: 0;
    padding: 0 4rem 0 2.4rem;
    position: relative;
    display: flex;
    align-items: center;
    font-size: 1.4rem;
    line-height: 1.7rem;
    color: $c-dark;
    user-select: none;

    // The clear cross sits at right: 4.4rem, so the value needs extra room not to run under it
    &--clearable {
      padding-right: 6rem;
    }

    &--tight {
      padding: 0 3.2rem 0 1.6rem;
    }

    &--mid {
      height: 3.1rem;
      padding: 0 3.2rem 0 1.6rem;
      font-size: 1.2rem;
      line-height: 1.2rem;
    }

    &--min-code {
      height: 2.5rem;
      padding: 0 2.4rem 0 0.8rem;
      font-size: 1.2rem;
      line-height: 1.2rem;
    }
  }

  &__selected-value {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &--placeholder {
      color: rgba($c-dark, 0.5);
    }
  }

  &__cross-icon {
    position: absolute;
    top: calc(50% - 4px);
    right: 4.4rem;
  }

  &__arrow {
    width: 0.9rem;
    height: 0.5rem;
    position: absolute;
    top: calc(50% - 2px);
    right: 2.4rem;
    pointer-events: none;

    &--tight {
      right: 1.6rem;
    }

    &--tighter {
      right: 1.2rem;
    }
  }
}
</style>
